/**
 * canonicalItemCost — the single policy that answers "what does this canonical
 * inventory item cost right now, per its own count unit, and why?".
 *
 * One module, two runtimes. The browser (`app/javascript/utils/itemCostSummary.js`)
 * and the edge functions (`menu-recipe-costing`) both import THIS file, so the
 * number a recipe is costed at and the number the item header displays can
 * never drift apart. There is deliberately no second mirror to keep in sync.
 *
 * What it does NOT do: choose a vendor. A recipe matches ONE canonical
 * inventory item; the purchase option recorded alongside the cost is
 * provenance — evidence for where the canonical cost came from — and never
 * part of the recipe's identity. Nothing here compares vendors, prefers the
 * cheapest, or switches a recipe's supplier.
 *
 * Money is returned UNROUNDED. A cost we cannot derive is reported as
 * `available: false` with a reason code; it is never reported as 0.
 */

/* ── numbers ─────────────────────────────────────────────────────────────── */

export const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const toPositiveNumber = (value) => {
  const parsed = toFiniteNumber(value);
  return parsed !== null && parsed > 0 ? parsed : null;
};

const timeOf = (value) => {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/* ── Costing methods ─────────────────────────────────────────────────────── */

/**
 * How the current inventory cost is derived. `last_purchase` is the system
 * default: inventory_items.verified_unit_cost is written only when a verified
 * purchase clears the cost gate, so "the cost we hold" IS "the last verified
 * purchase" unless a store configures otherwise.
 */
export const COSTING_METHODS = Object.freeze({
  last_purchase: Object.freeze({
    key: 'last_purchase',
    label: 'Last purchase cost',
    description: 'The cost from the most recent completed purchase.',
  }),
  weighted_average: Object.freeze({
    key: 'weighted_average',
    label: 'Weighted average',
    description: 'The quantity-weighted average of completed purchases.',
  }),
  preferred_vendor: Object.freeze({
    key: 'preferred_vendor',
    label: 'Preferred-vendor cost',
    description: "The preferred vendor product's normalized price.",
  }),
  standard: Object.freeze({
    key: 'standard',
    label: 'Standard cost',
    description: 'A cost entered and held manually.',
  }),
  // Prepared inventory items: the cost is published by the sub-recipe system
  // into verified_unit_cost (source `sub_recipe`) and never entered by hand.
  // Set on the item by the prepared-item setup flow, so a prepared item must
  // not read back as "Last purchase cost" — nobody purchased it.
  sub_recipe: Object.freeze({
    key: 'sub_recipe',
    label: 'Sub-recipe cost',
    description: "Derived from the item's source sub-recipe batch cost.",
  }),
});

export const DEFAULT_COSTING_METHOD = 'last_purchase';

/**
 * Resolves the costing method for an item: the item's own override first,
 * then the store setting, then the system default. Unknown values fall back
 * to the default rather than rendering a label nobody configured.
 */
export function resolveCostingMethod(item, store) {
  const raw = item?.costing_method ?? store?.inventory_costing_method ?? null;
  const key = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
  return COSTING_METHODS[key] || COSTING_METHODS[DEFAULT_COSTING_METHOD];
}

/* ── Purchase options (the pure primitives the cost policy needs) ────────── */

/** Active options, whatever their approval/visibility flags. */
export function activeOptions(options) {
  return (options || []).filter((option) => option?.status === 'active');
}

/** The single preferred option, or null. */
export function preferredOption(options) {
  return activeOptions(options).find((option) => option.is_preferred === true) || null;
}

/**
 * Comparable price for one option, expressed per the item's count unit.
 *
 * An option is comparable only when its pack → count-unit conversion actually
 * resolves. 'unresolved' means we do not know how many count units are in the
 * package, so any per-unit number would be invented. Returns null in that
 * case — the caller discloses it as "needs unit review" rather than guessing.
 */
export function comparableUnitCost(option) {
  if (!option) return null;
  if (option.conversion_status === 'unresolved') return null;
  const stored = toPositiveNumber(option.normalized_unit_cost);
  if (stored !== null) return stored;
  const price = toPositiveNumber(option.package_price);
  const qty = toPositiveNumber(option.qty_per_package);
  if (price === null || qty === null) return null;
  return price / qty;
}

/**
 * Best available vendor label for an option, given a { [vendorId]: name } map.
 * Falls back to the raw vendor name read off the source document, which is
 * all an unlinked import has. Never returns a bare uuid.
 */
export function vendorLabelOf(option, vendorNameById = {}) {
  if (!option) return null;
  const linked = option.vendor_id != null ? vendorNameById[option.vendor_id] : null;
  if (linked) return String(linked);
  const raw = (option.vendor_name_text || '').trim();
  if (raw) return raw;
  if (option.vendor_id != null) return null; // id present but name not loaded yet
  const described = (option.vendor_description || '').trim();
  return described || null;
}

/* ── Purchase records ────────────────────────────────────────────────────── */

/**
 * Purchase kinds that represent money actually spent. A draft order, an
 * unreceived PO, an unmatched invoice line, an order-guide quote, and a
 * manual price entry are all excluded: none of them is a purchase.
 */
export const COMPLETED_PURCHASE_KINDS = Object.freeze([
  'invoice',
  'receipt',
  'shopping_trip',
  'allocation',
]);

const COMPLETED_KIND_SET = new Set(COMPLETED_PURCHASE_KINDS);

/** True when a normalized purchase record represents a completed purchase. */
export function isCompletedPurchase(record) {
  if (!record || !COMPLETED_KIND_SET.has(record.kind)) return false;
  if (timeOf(record.purchasedAt) === null) return false;
  return toPositiveNumber(record.packagePrice) !== null
    || toPositiveNumber(record.normalizedUnitCost) !== null;
}

const purchaseRecord = (fields) => ({
  optionId: null,
  vendorId: null,
  vendorNameText: null,
  vendorItemName: null,
  packDescription: null,
  packagePrice: null,
  normalizedUnitCost: null,
  quantity: null,
  purchasedAt: null,
  kind: null,
  source: null,
  ...fields,
});

/**
 * Purchase records implied by the options themselves. An option sourced from
 * an invoice / receipt / shopping trip AND marked verified is evidence of a
 * real purchase at its effective date; an order-guide or manual row is not.
 */
export function purchaseRecordsFromOptions(options) {
  return (options || [])
    .filter((option) => option
      && COMPLETED_KIND_SET.has(option.source_type)
      && option.verification_status === 'verified')
    .map((option) => purchaseRecord({
      optionId: option.id,
      vendorId: option.vendor_id ?? null,
      vendorNameText: option.vendor_name_text ?? null,
      vendorItemName: option.vendor_description ?? null,
      packDescription: option.pack_description ?? option.purchase_unit ?? null,
      packagePrice: toFiniteNumber(option.package_price),
      normalizedUnitCost: comparableUnitCost(option),
      purchasedAt: option.effective_date ?? null,
      kind: option.source_type,
      source: option.source_ref ?? option.source_type ?? null,
    }));
}

/**
 * Purchase records from the price_history ledger. Only rows that name the
 * purchase option they came from can be attributed to a vendor product, and
 * only rows whose option is itself purchase-sourced count as a purchase —
 * price_history.source records the UI channel, not whether money changed hands.
 */
export function purchaseRecordsFromPriceHistory(rows, options) {
  const byId = new Map((options || []).filter((o) => o?.id != null).map((o) => [String(o.id), o]));
  return (rows || [])
    .map((row) => {
      if (!row || row.purchase_option_id == null) return null;
      const option = byId.get(String(row.purchase_option_id));
      if (!option || !COMPLETED_KIND_SET.has(option.source_type)) return null;
      return purchaseRecord({
        optionId: option.id,
        vendorId: option.vendor_id ?? null,
        vendorNameText: option.vendor_name_text ?? null,
        vendorItemName: option.vendor_description ?? null,
        packDescription: option.pack_description ?? option.purchase_unit ?? null,
        packagePrice: toFiniteNumber(row.new_case_price ?? row.new_unit_price),
        normalizedUnitCost: toFiniteNumber(row.normalized_unit_cost),
        purchasedAt: row.effective_date ?? row.changed_at ?? null,
        kind: option.source_type,
        source: row.source ?? null,
      });
    })
    .filter(Boolean);
}

/**
 * Purchase records from CONFIRMED purchase allocation lines. Lines on a draft
 * or canceled allocation are plans, not purchases, and are dropped here.
 */
export function purchaseRecordsFromAllocationLines(lines) {
  return (lines || [])
    .filter((line) => line && (line.allocation_status ?? line.status) === 'confirmed')
    .map((line) => {
      const snapshot = line.option_snapshot || {};
      return purchaseRecord({
        optionId: line.selected_purchase_option_id ?? null,
        vendorId: line.vendor_id ?? snapshot.vendor_id ?? null,
        vendorNameText: snapshot.vendor_name_text ?? null,
        vendorItemName: snapshot.vendor_description ?? null,
        packDescription: snapshot.pack_description ?? snapshot.purchase_unit ?? null,
        packagePrice: toFiniteNumber(snapshot.package_price),
        normalizedUnitCost: toFiniteNumber(line.unit_cost ?? snapshot.normalized_unit_cost),
        quantity: toFiniteNumber(line.quantity_received ?? line.quantity_needed),
        purchasedAt: line.confirmed_at ?? line.updated_at ?? null,
        kind: 'allocation',
        source: 'purchase_allocation',
      });
    });
}

/**
 * The most recent completed purchase, or null. Deliberately never falls back
 * to the preferred vendor: an item nobody has bought yet has no last purchase,
 * and saying otherwise would invent a purchase record.
 */
export function lastPurchaseOf(records) {
  let best = null;
  let bestTime = null;
  (records || []).forEach((record) => {
    if (!isCompletedPurchase(record)) return;
    const time = timeOf(record.purchasedAt);
    if (bestTime === null || time > bestTime) {
      best = record;
      bestTime = time;
    }
  });
  return best;
}

/**
 * Quantity-weighted average unit cost across completed purchases. Needs both
 * a unit cost and a quantity on each record; returns null when no record
 * carries both, so the caller discloses that rather than averaging nothing.
 */
export function weightedAverageUnitCost(records) {
  let weighted = 0;
  let quantity = 0;
  (records || []).forEach((record) => {
    if (!isCompletedPurchase(record)) return;
    const cost = toPositiveNumber(record.normalizedUnitCost);
    const qty = toPositiveNumber(record.quantity);
    if (cost === null || qty === null) return;
    weighted += cost * qty;
    quantity += qty;
  });
  if (quantity <= 0) return null;
  return weighted / quantity;
}

/* ── Current canonical inventory cost ────────────────────────────────────── */

/**
 * Reasons a current inventory cost cannot be shown. Each maps to plain user
 * copy in the UI; none of them may render as $0.00.
 */
export const COST_UNAVAILABLE_REASONS = Object.freeze({
  no_cost_data: 'No completed purchase price and unit conversion are available.',
  no_preferred_vendor: 'No preferred vendor is selected, so there is no preferred-vendor cost.',
  no_comparable_prices: 'Pack size or unit conversion is missing for every purchase option.',
  no_purchase_history: 'No completed purchase price and unit conversion are available.',
  no_standard_cost: 'No standard cost has been entered for this item.',
});

/**
 * Source label for a cost carried over from the pre-purchase-options era —
 * inventory_items.unit_price with no verified cost behind it. A transitional
 * compatibility path, always labelled so no screen or snapshot can mistake it
 * for a verified canonical cost.
 */
export const LEGACY_COST_SOURCE = 'legacy_item_price';

/** Source label the prepared-item (sub-recipe) system publishes. */
export const SUB_RECIPE_COST_SOURCE = 'sub_recipe';

const heldCost = (item) => {
  const verified = toPositiveNumber(item?.verified_unit_cost);
  if (verified !== null) {
    return {
      amount: verified,
      sourceType: item.verified_cost_source ?? null,
      effectiveDate: item.verified_cost_effective_date ?? null,
      optionId: item.verified_cost_purchase_option_id ?? null,
      legacy: false,
    };
  }
  const legacyUnit = toPositiveNumber(item?.unit_price);
  if (legacyUnit !== null) {
    return {
      amount: legacyUnit,
      sourceType: LEGACY_COST_SOURCE,
      effectiveDate: item?.updated_at ?? null,
      optionId: null,
      legacy: true,
    };
  }
  return null;
};

/**
 * The single most prominent money value on an item: what inventory and
 * recipes are costed at, in the item's own count unit, under the configured
 * costing method — plus the provenance that explains where it came from.
 *
 * Falls back to the cost the system actually holds (verified_unit_cost) when
 * the configured method cannot be computed — but says so via `fellBack` and
 * reports the source ACTUALLY used, so nothing ever claims a weighted average
 * it did not compute.
 *
 * The vendor fields are an explanatory snapshot of the purchase option that
 * supplied the cost. They do not participate in recipe identity, matching or
 * search, and a recipe never stores them as its supplier.
 *
 * @param {object}  args.item            canonical inventory_items row
 * @param {Array}  [args.options]        purchase_options rows for the item
 * @param {Array}  [args.purchases]      normalized purchase records
 * @param {object} [args.store]          store row (costing-method setting)
 * @param {object} [args.vendorNameById] { [vendorId]: display_name }
 * @returns {object} see `base` below plus the canonical provenance fields.
 */
export function resolveCanonicalInventoryCost({
  item,
  options,
  purchases,
  store,
  vendorNameById = {},
  now = Date.now(),
} = {}) {
  const method = resolveCostingMethod(item, store);
  const unit = item?.unit || null;
  const held = heldCost(item);
  const warnings = [];

  const base = {
    available: false,
    amount: null,
    unit,
    method,
    reason: 'no_cost_data',
    reasonText: COST_UNAVAILABLE_REASONS.no_cost_data,
    sourceType: null,
    effectiveDate: null,
    optionId: null,
    fellBack: false,
  };

  const settle = (amount, extra, reason) => {
    if (amount === null) {
      // The configured method could not be computed. Show the cost the system
      // actually holds rather than a zero, and flag the fallback.
      if (held) {
        warnings.push(
          `Costing method "${method.key}" could not be computed (${reason}); used the held ${held.sourceType || 'canonical'} cost instead.`,
        );
        return {
          ...base,
          available: true,
          amount: held.amount,
          sourceType: held.sourceType,
          effectiveDate: held.effectiveDate,
          optionId: held.optionId,
          reason,
          reasonText: COST_UNAVAILABLE_REASONS[reason] || null,
          fellBack: true,
        };
      }
      return {
        ...base,
        reason,
        reasonText: COST_UNAVAILABLE_REASONS[reason] || COST_UNAVAILABLE_REASONS.no_cost_data,
      };
    }
    return { ...base, available: true, amount, reason: null, reasonText: null, ...extra };
  };

  let resolved;
  switch (method.key) {
    case 'preferred_vendor': {
      const preferred = preferredOption(options);
      const cost = preferred ? comparableUnitCost(preferred) : null;
      resolved = settle(
        cost,
        {
          sourceType: preferred?.source_type ?? null,
          effectiveDate: preferred?.effective_date ?? null,
          optionId: preferred?.id ?? null,
        },
        preferred ? 'no_comparable_prices' : 'no_preferred_vendor',
      );
      break;
    }
    case 'weighted_average': {
      const average = weightedAverageUnitCost(purchases);
      const latest = lastPurchaseOf(purchases);
      resolved = settle(
        average,
        { sourceType: 'weighted_average', effectiveDate: latest?.purchasedAt ?? null, optionId: null },
        'no_purchase_history',
      );
      break;
    }
    case 'standard': {
      const standard = toPositiveNumber(item?.standard_unit_cost);
      resolved = settle(
        standard,
        { sourceType: 'standard', effectiveDate: item?.updated_at ?? null, optionId: null },
        'no_standard_cost',
      );
      break;
    }
    case 'sub_recipe':
    case 'last_purchase':
    default: {
      // The held VERIFIED cost IS the last verified purchase (and, for a
      // prepared item, the sub-recipe cost the batch system published); prefer
      // it so the number here matches what counts and recipes actually use.
      //
      // The held LEGACY price is different: it is a transitional column with
      // no purchase behind it, so under a method named "last purchase" an
      // actual completed purchase outranks it. The legacy price answers only
      // when there is no verified cost AND no completed purchase — exactly
      // as labelled ("until a purchase verifies this cost").
      if (held && !held.legacy) {
        resolved = settle(held.amount, {
          sourceType: held.sourceType,
          effectiveDate: held.effectiveDate,
          optionId: held.optionId,
        });
        break;
      }
      const latest = lastPurchaseOf(purchases);
      const cost = latest ? toPositiveNumber(latest.normalizedUnitCost) : null;
      if (cost !== null) {
        resolved = settle(cost, {
          sourceType: latest?.kind ?? null,
          effectiveDate: latest?.purchasedAt ?? null,
          optionId: latest?.optionId ?? null,
        });
        break;
      }
      if (held) {
        // Legacy price, no purchase to outrank it — byte-identical to the
        // pre-change behavior for legacy-only items.
        resolved = settle(held.amount, {
          sourceType: held.sourceType,
          effectiveDate: held.effectiveDate,
          optionId: held.optionId,
        });
        break;
      }
      resolved = settle(
        null,
        {
          sourceType: null,
          effectiveDate: null,
          optionId: null,
        },
        'no_purchase_history',
      );
      break;
    }
  }

  // ── provenance: which vendor product supplied this cost, if any.
  const sourceOption = resolved.optionId != null
    ? (options || []).find((o) => o && String(o.id) === String(resolved.optionId)) || null
    : null;
  if (resolved.optionId != null && !sourceOption) {
    warnings.push('The purchase option that supplied this cost is no longer loadable.');
  }

  const isLegacyFallback = resolved.sourceType === LEGACY_COST_SOURCE;
  if (isLegacyFallback) {
    warnings.push('No verified canonical cost exists — using the legacy item price.');
  }

  return {
    ...resolved,
    // ── canonical contract (the shape recipe costing consumes) ──
    inventoryItemId: item?.id ?? null,
    inventoryUnit: unit,
    amountPerInventoryUnit: resolved.amount,
    costingMethod: method.key,
    effectiveAt: resolved.effectiveDate,
    sourcePurchaseOptionId: resolved.optionId ?? null,
    sourceVendorId: sourceOption?.vendor_id ?? null,
    sourceVendorName: sourceOption ? vendorLabelOf(sourceOption, vendorNameById) : null,
    sourceVendorProductName: sourceOption?.vendor_description ?? null,
    isLegacyFallback,
    isStandardCost: resolved.sourceType === 'standard',
    isPreparedItemCost: resolved.sourceType === SUB_RECIPE_COST_SOURCE,
    warnings,
    now,
  };
}

/**
 * Legacy alias. `currentInventoryCost` is the name the browser has always
 * used; it is the same function, so there is exactly one policy.
 */
export const currentInventoryCost = resolveCanonicalInventoryCost;
